import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';

const PM_REVIEW_FIELDS = `
  ItemId
  CreatedDate
  LastUpdatedDate
  AssessmentType
  ReviewForId
  Name
  DesignationId
  DepartmentId
  DepartmentName
  DepartmentKey
  PeerList
  Self
  Annual
  Confirmation
  HalfYearly
  Special
  Management
  ConfirmationForIntern
  PeerSelectedStatus
  UserHRPeriodicalProfileId
  SelfOrPeerReviewSummary
  AiStrengthsSummary
  AiAreasForImprovementSummary
  IsAttachPeerForms
  IsAttachSelfForm
  ReviewDataJson
  PeerManagerIds
`;

/** Everything a list or a status calculation needs; the (large) form snapshot is fetched separately. */
const PM_REVIEW_DETAIL_LIGHT_FIELDS = `
  ItemId
  CreatedDate
  LastUpdatedDate
  PmReviewId
  AssessmentType
  ReviewType
  Name
  RequestedDateUtc
  PeersDeadLine
  Timezone
  PeerUserId
  ReportingManagerId
  Category
  DesignationId
  DepartmentId
  DepartmentName
  DepartmentKey
  Comment
  UpdateComment
  Status
  SuggestedTier
  PromotionRecommendation
  IsMbmSent
  ProjectIds
`;

const PM_REVIEW_DETAIL_FULL_FIELDS = `
  ${PM_REVIEW_DETAIL_LIGHT_FIELDS}
  PmFormJson
  IsMissedDeadlineNotified
`;

const PM_FORM_CANDIDATE_FIELDS = `
  ItemId
  Title
  Description
  AssessmentType
  ReviewType
  Category
  IsAllDepartments
  DepartmentIds
  IsAllDesignations
  DesignationIds
  ConfirmationForIntern
  HasSections
  FormDataJson
  SectionsJson
`;

const pagingEnvelope = (fields: string) => `
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items {
        ${fields}
      }
`;

/** Latest change first: the default order of the review and review-detail lists. */
export const DEFAULT_PM_REVIEWS_ORDER: OrderBy[] = [
  { field: 'LastUpdatedDate', direction: 'DESC' },
];

const queryArgs = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order = '[]'
) => `
      where: ${buildWhereLiteral(where)}
      order: ${order}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
`;

/** Latest change first by default, like the old `GetPmReviews` / `GetPmReviewsForBuh` `{ LastUpdateDate: -1 }` sort. */
export const buildGetPmReviewsQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_PM_REVIEWS_ORDER
) => `
  query {
    PmReviews: getPmReviews(${queryArgs(where, pageNo, pageSize, buildOrderLiteral(order))}) {${pagingEnvelope(PM_REVIEW_FIELDS)}}
  }
`;

/** Latest change first by default, like the old `GetPmReviewDetailByUser` `{ LastUpdateDate: -1 }` sort. */
export const buildGetPmReviewDetailsQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_PM_REVIEWS_ORDER
) => `
  query {
    PmReviewDetails: getPmReviewDetails(${queryArgs(where, pageNo, pageSize, buildOrderLiteral(order))}) {${pagingEnvelope(PM_REVIEW_DETAIL_LIGHT_FIELDS)}}
  }
`;

export const buildGetPmReviewDetailsFullQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    PmReviewDetails: getPmReviewDetails(${queryArgs(where, pageNo, pageSize)}) {${pagingEnvelope(PM_REVIEW_DETAIL_FULL_FIELDS)}}
  }
`;

export const buildGetPmFormCandidatesQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    PmForms: getPmForms(${queryArgs(where, pageNo, pageSize)}) {
      totalCount
      items {
        ${PM_FORM_CANDIDATE_FIELDS}
      }
    }
  }
`;
